import { useState, useEffect } from 'react';
import { X, Clock, Zap, ArrowRight, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const LimitedTimeOffer = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(true);
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    // Check if user dismissed the banner
    const dismissed = localStorage.getItem('offer_banner_dismissed');
    if (dismissed) {
      const dismissedTime = new Date(dismissed).getTime();
      const now = new Date().getTime();
      // Show again after 24 hours
      if (now - dismissedTime < 24 * 60 * 60 * 1000) {
        setIsVisible(false);
        return;
      }
    }

    // Set end date to 3 days from now (or use a fixed date)
    const getEndDate = () => {
      const stored = localStorage.getItem('offer_end_date');
      if (stored) {
        return new Date(stored);
      }
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + 3);
      endDate.setHours(23, 59, 59, 999);
      localStorage.setItem('offer_end_date', endDate.toISOString());
      return endDate;
    };

    const endDate = getEndDate();

    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const distance = endDate.getTime() - now;

      if (distance < 0) {
        // Reset offer for another 3 days
        localStorage.removeItem('offer_end_date');
        return {
          days: 3,
          hours: 0,
          minutes: 0,
          seconds: 0
        };
      }

      return {
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000)
      };
    };

    setTimeLeft(calculateTimeLeft());

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const dismissBanner = () => {
    setIsVisible(false);
    localStorage.setItem('offer_banner_dismissed', new Date().toISOString());
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Top Banner */}
      <div className="fixed top-0 left-0 right-0 z-[60] overflow-hidden">
        <div className="relative bg-gradient-to-r from-[#F97316] via-[#EC4899] to-[#8B5CF6] py-3 px-4">
          {/* Animated Background */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -inset-[100%] animate-[spin_20s_linear_infinite]">
              {[...Array(20)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-32 h-32 bg-white/5 rounded-full"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                  }}
                />
              ))}
            </div>
          </div>

          <div className="relative max-w-7xl mx-auto flex items-center justify-center gap-4 md:gap-8 flex-wrap">
            {/* Sparkle Icon */}
            <div className="hidden sm:flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-yellow-300 animate-pulse" />
              <span className="text-white/90 font-medium text-sm">LIMITED TIME</span>
            </div>

            {/* Main Offer Text */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full">
                <Zap className="w-4 h-4 text-yellow-300" fill="currentColor" />
                <span className="text-white font-black text-lg md:text-xl">40% OFF</span>
              </div>
              <span className="text-white font-semibold text-sm md:text-base">
                All Services & Packages
              </span>
            </div>

            {/* Countdown Timer */}
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-white/80" />
              <div className="flex items-center gap-1">
                <TimeBlock value={timeLeft.days} label="D" />
                <span className="text-white/60 font-bold">:</span>
                <TimeBlock value={timeLeft.hours} label="H" />
                <span className="text-white/60 font-bold">:</span>
                <TimeBlock value={timeLeft.minutes} label="M" />
                <span className="text-white/60 font-bold">:</span>
                <TimeBlock value={timeLeft.seconds} label="S" />
              </div>
            </div>

            {/* CTA Button */}
            <button
              onClick={() => navigate('/book-consultation')}
              className="hidden md:flex items-center gap-2 px-4 py-2 bg-white text-gray-900 font-bold text-sm rounded-full hover:bg-yellow-300 transition-all duration-300 hover:scale-105 shadow-lg"
            >
              Claim Offer
              <ArrowRight size={16} />
            </button>

            {/* Close Button */}
            <button
              onClick={dismissBanner}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-white/60 hover:text-white hover:bg-white/10 rounded-full transition-colors"
              aria-label="Dismiss offer"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Spacer to push content down */}
      <div className="h-[52px]"></div>
    </>
  );
};

const TimeBlock = ({ value, label }) => (
  <div className="flex items-center gap-0.5 bg-black/20 backdrop-blur-sm px-2 py-1 rounded">
    <span className="text-white font-mono font-bold text-sm md:text-base min-w-[24px] text-center">
      {String(value).padStart(2, '0')}
    </span>
    <span className="text-white/60 text-[10px] font-medium">{label}</span>
  </div>
);

export default LimitedTimeOffer;
